import { Difficulty } from '@all-for-one/shared-types'
import { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { exportTableToPdf, exportTableToXlsx, svgUrlToPngDataUrl } from 'util/export'
import firecrackerLogo from 'img/logo-mark.png'

import { Class, StudentEntry } from '@all-for-one/shared-types'

interface ClassContextType {
    classId: string;
    className: string;
    students: StudentEntry[];
    updateStudents: (newStudents: StudentEntry[]) => void;
    addStudent: (student: StudentEntry) => void;
    removeStudent: (studentId: string) => void;
    updateStudentDifficulty: (studentId: string, difficulty: Difficulty) => void;
    handleDownloadXLS: () => void;
    handleDownloadPDF: () => Promise<void>;
}

export const ClassContext = createContext<ClassContextType | null>(null)

export const useClassContext = () => {
    const context = useContext(ClassContext)
    if (!context) {
        throw new Error('useClass must be used within a ClassContextProvider')
    }
    return context
}

interface ClassContextProviderProps {
    children: ReactNode;
    initialClass: Class;
}

export const ClassContextProvider = ({ children, initialClass }: ClassContextProviderProps) => {
    const [students, setStudents] = useState<StudentEntry[]>(initialClass.students || [])

    // Sync context state when RTK Query refetches new data
    useEffect(() => {
        setStudents(initialClass.students || [])
    }, [initialClass.students])

    const updateStudents = (newStudents: StudentEntry[]) => {
        setStudents(newStudents)
    }

    const addStudent = (student: StudentEntry) => {
        setStudents(prev => [...prev, student])
    }

    const removeStudent = (studentId: string) => {
        setStudents(prev => prev.filter(student => student.studentId !== studentId))
    }

    const updateStudentDifficulty = (studentId: string, difficulty: Difficulty) => {
        setStudents(prev => prev.map(student => 
            student.studentId === studentId 
                ? { ...student, difficulty } 
                : student
        ))
    }

    const handleDownloadXLS = () => {
        console.log('Download in XLS')
        if (students.length > 0) {
            const headers = ['Name', 'User ID', 'Student ID', 'Grade', 'Overall Lesson Average', 'Current Level']
            const rows = students.map(student => [
                student.studentName,
                student.studentId,
                student.schoolStudentId ?? '',
                student.grade ?? '',
                student.lessonAverage != null ? Number(student.lessonAverage).toFixed(1) : '',
                student.difficulty
            ])

            exportTableToXlsx({
                fileName: `${initialClass.name}_students.xlsx`,
                sheetName: 'Students',
                headers,
                rows
            })
        }
    }

    const handleDownloadPDF = async () => {
        console.log('Download in PDF')
        if (students.length > 0) {
            const headers = ['Name', 'User ID', 'Student ID', 'Grade', 'Overall Lesson Average', 'Current Level']
            const rows = students.map(student => [
                student.studentName,
                student.studentId,
                student.schoolStudentId ?? '',
                student.grade ?? '',
                student.lessonAverage != null ? Number(student.lessonAverage).toFixed(1) : '',
                student.difficulty
            ])

            const logoDataUrl = await svgUrlToPngDataUrl(firecrackerLogo, 52, 52)
            const passwordLine = initialClass.defaultPassword ? `Default Password: ${initialClass.defaultPassword}` : ''

            exportTableToPdf({
                fileName: `${initialClass.name}_students.pdf`,
                title: `${initialClass.name} - Student List`,
                headers,
                rows,
                styles: { fontSize: 10, cellPadding: 3 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                branding: {
                    logoDataUrl,
                    titleLines: [passwordLine].filter(Boolean)
                }
            })
        }
    }

    return (
        <ClassContext.Provider value={{
            classId: initialClass.classId,
            className: initialClass.name,
            students,
            updateStudents,
            addStudent,
            removeStudent,
            updateStudentDifficulty,
            handleDownloadXLS,
            handleDownloadPDF
        }}>
            {children}
        </ClassContext.Provider>
    )
}